import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, Search, ArrowLeft, SlidersHorizontal, Home } from "lucide-react";
import apiClient from "@/lib/api";
import { setMiniSiteContext } from "@/lib/minisiteContext";
import { PROPERTY_TYPE_LABELS } from "@/components/property/PropertyStatusBadge";
import MiniSitePropertyCard from "@/components/minisite/MiniSitePropertyCard";

const SORTS = [
  { value: "recent", label: "Terbaru" },
  { value: "price_asc", label: "Harga Termurah" },
  { value: "price_desc", label: "Harga Tertinggi" },
  { value: "rating", label: "Rating Tertinggi" },
];

const PAGE_SIZE = 12;

export default function MiniSitePropertiesPage() {
  const { slug } = useParams();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, cities: [], property_types: [], owner_name: "", has_more: false });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [page, setPage] = useState(1);

  // Filters
  const [search, setSearch] = useState("");
  const [ptype, setPtype] = useState("");
  const [city, setCity] = useState("");
  const [availability, setAvailability] = useState("");
  const [sort, setSort] = useState("recent");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  const buildParams = useCallback((pageNum) => {
    const p = { page: pageNum, page_size: PAGE_SIZE, sort };
    if (search.trim()) p.search = search.trim();
    if (ptype) p.property_type = ptype;
    if (city) p.city = city;
    if (availability) p.availability = availability;
    if (minPrice) p.min_price = Number(minPrice);
    if (maxPrice) p.max_price = Number(maxPrice);
    return p;
  }, [search, ptype, city, availability, sort, minPrice, maxPrice]);

  const fetchPage = useCallback((pageNum, append) => {
    const setter = append ? setLoadingMore : setLoading;
    setter(true);
    apiClient
      .get(`/public/minisite/${slug}/properties`, { params: buildParams(pageNum) })
      .then((r) => {
        setMeta({
          total: r.data.total, cities: r.data.cities, property_types: r.data.property_types,
          owner_name: r.data.owner_name, has_more: r.data.has_more,
        });
        setMiniSiteContext({ slug, owner_name: r.data.owner_name });
        setItems((prev) => (append ? [...prev, ...r.data.items] : r.data.items));
        setPage(pageNum);
      })
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); })
      .finally(() => setter(false));
  }, [slug, buildParams]);

  // Refetch from page 1 whenever filters change (debounced for search).
  useEffect(() => {
    document.title = "Semua Hunian — AVASA Coliving";
    setMiniSiteContext({ slug });
    const t = setTimeout(() => fetchPage(1, false), 300);
    return () => clearTimeout(t);
  }, [fetchPage]);

  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6 text-center">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-2">Web-site tidak ditemukan</h1>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">
            <Home size={16} /> Ke Beranda AVASA
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="minisite-properties-page">
      {/* Header */}
      <header className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to={`/properti/${slug}`} data-testid="minisite-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-foreground/80 hover:text-primary-foreground mb-4">
            <ArrowLeft size={15} /> Kembali ke web-site {meta.owner_name}
          </Link>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Semua Hunian</h1>
          <p className="mt-1 text-primary-foreground/80 text-sm" data-testid="minisite-total">{meta.total} hunian ditemukan</p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-4 gap-6">
        {/* Filters */}
        <aside className="lg:col-span-1">
          <div className="bg-card border border-border rounded-2xl p-5 lg:sticky lg:top-6 space-y-4" data-testid="minisite-filters">
            <div className="flex items-center gap-2 font-heading font-semibold"><SlidersHorizontal size={16} className="text-primary" /> Filter</div>

            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="minisite-search"
                placeholder="Cari nama/lokasi..."
                className="w-full rounded-xl border border-border bg-background pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
            </div>

            <Field label="Tipe Hunian">
              <select value={ptype} onChange={(e) => setPtype(e.target.value)} data-testid="minisite-filter-type" className={selectCls}>
                <option value="">Semua Tipe</option>
                {meta.property_types.map((t) => <option key={t} value={t}>{PROPERTY_TYPE_LABELS[t] || t}</option>)}
              </select>
            </Field>

            <Field label="Kota / Area">
              <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="minisite-filter-city" className={selectCls}>
                <option value="">Semua Area</option>
                {meta.cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>

            <Field label="Ketersediaan">
              <select value={availability} onChange={(e) => setAvailability(e.target.value)} data-testid="minisite-filter-availability" className={selectCls}>
                <option value="">Semua</option>
                <option value="available">Ada kamar tersedia</option>
                <option value="full">Penuh</option>
              </select>
            </Field>

            <Field label="Rentang Harga (Rp/bln)">
              <div className="flex items-center gap-2">
                <input type="number" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} data-testid="minisite-filter-min" placeholder="Min" className={inputCls} />
                <span className="text-muted-foreground text-sm">–</span>
                <input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} data-testid="minisite-filter-max" placeholder="Max" className={inputCls} />
              </div>
            </Field>

            <Field label="Urutkan">
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="minisite-sort" className={selectCls}>
                {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
          </div>
        </aside>

        {/* Results */}
        <main className="lg:col-span-3">
          {loading ? (
            <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-primary" size={28} /></div>
          ) : items.length === 0 ? (
            <div className="py-20 text-center text-muted-foreground" data-testid="minisite-no-results">
              Tidak ada hunian yang cocok dengan filter Anda.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6" data-testid="minisite-results-grid">
                {items.map((p, i) => <MiniSitePropertyCard key={p.id} card={p} index={i} />)}
              </div>
              {meta.has_more && (
                <div className="mt-8 text-center">
                  <button onClick={() => fetchPage(page + 1, true)} disabled={loadingMore} data-testid="minisite-load-more"
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-primary/40 text-primary font-semibold hover:bg-primary/5 transition-colors disabled:opacity-60">
                    {loadingMore ? <Loader2 size={16} className="animate-spin" /> : null} Muat Lebih Banyak
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

const selectCls = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30";
const inputCls = "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-muted-foreground mb-1">{label}</label>
      {children}
    </div>
  );
}
